import {
  Box,
  Divider,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import type { QuickView } from 'lib-analytics-types';
import { formatMillisecondsToDuration } from 'lib-analytics-ui';
import type { SearchUser } from 'lib-search-types';
import { useHasAccess } from 'lib-shared-access-control';
import {
  HookFormCheckbox,
  HookFormSelectionListSingle,
  HookFormText,
  HookFormTextarea,
} from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { SubmitHandler, useForm } from 'react-hook-form';

import { analyticsApi } from 'apis/analytics';
import { usersApi } from 'apis/users';
import { HookFormUserSelect } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './quickViewModal.module.scss';
import { AnalyticsQuickViewModalType } from './quickViewModal.types';

const formId = 'analytics-quick-view-form';

type FieldValues = Pick<
  QuickView,
  'name' | 'description' | 'shared' | 'options'
> & {
  /**
   * The user that owns the quick view.
   *
   * @note Represented as an array to be compatible with the {@link HookFormUserSelect} component.
   */
  user: SearchUser[];
};

const autoRefreshIntervals = [30000, 60000, 300000, 900000, 3600000];

/**
 * A modal what for creating, updating, and duplicating quick views in
 * Analytics.
 */
export const AnalyticsQuickViewModal = createAppModal(
  { type: 'analytics:quickView' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const hasAnalyticsAccess = useHasAccess('analytics');
    const flags = useFeatureFlag(['analytics-custom-dashboards']);

    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const { mode = 'create', quickView } = modal.arg;

    const [createQuickView, createState] =
      analyticsApi.useCreateQuickViewMutation();
    const [updateQuickView, updateState] =
      analyticsApi.useUpdateQuickViewMutation();

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        name: quickView
          ? mode === 'duplicate'
            ? `${quickView.name} (copy)`
            : quickView.name
          : '',
        description: quickView?.description ?? '',
        shared: quickView?.shared ?? false,
        options: quickView?.options ?? undefined,
        ...(quickView?.user && { user: [quickView.user] }),
      },
    });

    /** Handles creating a new quick view. */
    const handleCreateQuickView = async (values: FieldValues) => {
      if (mode !== 'create') return;

      try {
        const {
          data: { quick_view: createdQuickView },
        } = await createQuickView({
          quickView: {
            ...values,
            panels: modal.arg.panels ?? [],
          },
        }).unwrap();

        ampli.analyticsQuickViewCreated({
          shared: values.shared,
        });

        ampli.analyticsQuickViewSharingToggled({
          shared: values.shared,
        });

        const { refresh_interval } = values.options ?? {};

        if (refresh_interval) {
          ampli.analyticsQuickViewAutoRefreshEnabled({
            interval: formatMillisecondsToDuration(refresh_interval, {
              compact: true,
            }),
            shared: values.shared,
          });
        }

        meta?.resolve({ quickView: createdQuickView });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `"${createdQuickView.name}" has been created.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
            leftAction: {
              text: 'See all saved dashboards',
              action: () => {
                navigate({ path: '/analytics/dashboards' });
              },
            },
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while saving the dashboard.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles updating an existing quick view. */
    const handleUpdateQuickView = async (values: FieldValues) => {
      if (!quickView) return;

      try {
        const { user, ...restValues } = values;

        const {
          data: { quick_view: updatedQuickView },
        } = await updateQuickView({
          id: quickView.id,
          quickView: {
            ...restValues,
          },
        }).unwrap();

        ampli.analyticsQuickViewUpdated({
          shared: values.shared,
        });

        // Track only if the sharing status has changed.
        if (quickView.shared !== updatedQuickView.shared) {
          ampli.analyticsQuickViewSharingToggled({
            shared: values.shared,
          });
        }

        const { refresh_interval } = values.options ?? {};

        if (
          refresh_interval &&
          refresh_interval !== quickView.options?.refresh_interval
        ) {
          ampli.analyticsQuickViewAutoRefreshEnabled({
            interval: formatMillisecondsToDuration(refresh_interval, {
              compact: true,
            }),
            shared: values.shared,
          });
        } else if (!refresh_interval && quickView.options?.refresh_interval) {
          ampli.analyticsQuickViewAutoRefreshDisabled({
            interval: formatMillisecondsToDuration(
              quickView.options?.refresh_interval,
              { compact: true },
            ),
            shared: values.shared,
          });
        }

        meta?.resolve({ quickView: updatedQuickView });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `Changes to "${values.name}" have been saved.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while updating the dashboard.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles creating a new quick view based on an existing one. */
    const handleDuplicateQuickView = async (values: FieldValues) => {
      if (!quickView) return;

      try {
        const { user, ...restValues } = values;

        const {
          data: { quick_view: createdQuickView },
        } = await createQuickView({
          quickView: {
            ...restValues,
            panels: quickView.panels,
          },
        }).unwrap();

        ampli.analyticsQuickViewDuplicated({
          shared: values.shared,
        });

        ampli.analyticsQuickViewSharingToggled({
          shared: values.shared,
        });

        meta?.resolve({ quickView: createdQuickView });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `"${quickView.name}" has been copied as "${createdQuickView.name}".`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while copying the dashboard.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles transferring ownership of an existing quick view. */
    const handleTransferOwnership = async (values: FieldValues) => {
      if (!quickView) return;

      try {
        const { user, ...restValues } = values;

        const {
          data: { quick_view: updatedQuickView },
        } = await updateQuickView({
          id: quickView.id,
          userId: user[0].id,
          quickView: {
            ...restValues,
          },
        }).unwrap();

        // Track only if the owner has changed.
        if (quickView.user.id !== updatedQuickView.user.id) {
          ampli.analyticsQuickViewOwnershipTransferred();
        }

        meta?.resolve({ quickView: updatedQuickView });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `Ownership of "${values.name}" has been transferred.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while transferring ownership of the dashboard.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles valid form submit. */
    const handleValidSubmit: SubmitHandler<FieldValues> = (values) => {
      if (mode === 'create') {
        handleCreateQuickView(values);
      } else if (mode === 'update') {
        handleUpdateQuickView(values);
      } else if (mode === 'duplicate') {
        handleDuplicateQuickView(values);
      } else if (mode === 'transferOwnership') {
        handleTransferOwnership(values);
      }
    };

    /** Various texts according to the mode of the modal. */
    const texts: Record<
      AnalyticsQuickViewModalType,
      Record<'title' | 'button', string>
    > = {
      create: {
        title: 'Save dashboard',
        button: 'Save dashboard',
      },
      duplicate: {
        title: 'Duplicate dashboard',
        button: 'Duplicate dashboard',
      },
      update: {
        title: 'Edit dashboard',
        button: 'Save dashboard',
      },
      transferOwnership: {
        title: 'Transfer dashboard ownership',
        button: 'Transfer ownership',
      },
    };

    const fieldRules: Record<
      keyof FieldValues,
      Record<'visible' | 'disabled', boolean>
    > = {
      name: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      description: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      shared: {
        visible: hasAnalyticsAccess && mode !== 'transferOwnership',
        disabled: false,
      },
      options: {
        visible:
          flags.analyticsCustomDashboards && mode !== 'transferOwnership',
        disabled: false,
      },
      user: {
        visible: mode === 'transferOwnership',
        disabled: false,
      },
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title={texts[mode].title} onClose={hide} />

        <ModalBody>
          <form
            id={formId}
            noValidate
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              {fieldRules.name.visible && (
                <FormField>
                  <HookFormText
                    name="name"
                    label="Name"
                    control={form.control}
                    rules={{
                      required: 'Name is required',
                    }}
                    componentProps={{
                      autoFocus: true,
                    }}
                    disabled={fieldRules.name.disabled}
                  />
                </FormField>
              )}

              {fieldRules.description.visible && (
                <FormField>
                  <HookFormTextarea
                    name="description"
                    label="Description"
                    control={form.control}
                    componentProps={{
                      height: 102,
                    }}
                    disabled={fieldRules.description.disabled}
                  />
                </FormField>
              )}

              {fieldRules.shared.visible && (
                <FormField>
                  <Box margin="0 0 300">
                    <HookFormCheckbox
                      name="shared"
                      label="Make this dashboard public"
                      control={form.control}
                      helpIcon={{
                        tooltipClassName: styles.helpTooltip,
                        text: (
                          <Text.Paragraph variant="2" color="inherit">
                            Select the checkbox to make the dashboard available
                            to all users with full access to Analytics. You can
                            always change this again later.
                          </Text.Paragraph>
                        ),
                      }}
                      disabled={fieldRules.shared.disabled}
                    />
                  </Box>
                </FormField>
              )}

              {fieldRules.options.visible && (
                <>
                  <FormField>
                    <Divider>
                      <Text.Paragraph as="span" color="tertiary">
                        Other settings
                      </Text.Paragraph>
                    </Divider>
                  </FormField>

                  <FormField>
                    <HookFormSelectionListSingle
                      name="options.refresh_interval"
                      label="Auto refresh interval"
                      control={form.control}
                      componentProps={{
                        clearable: true,
                        placeholder: 'Off',
                        getItemTitleByValue: (interval) =>
                          interval
                            ? formatMillisecondsToDuration(interval, {
                                compact: true,
                              })
                            : 'Off',
                      }}
                      helpIcon={{
                        tooltipClassName: styles.helpTooltip,
                        text: (
                          <Text.Paragraph variant="2" color="inherit">
                            The data in the dashboard will refresh automatically
                            at the selected interval.
                          </Text.Paragraph>
                        ),
                      }}
                    >
                      {autoRefreshIntervals.map((interval) => (
                        <SelectionList.Item key={interval} value={interval}>
                          {formatMillisecondsToDuration(interval, {
                            compact: true,
                          })}
                        </SelectionList.Item>
                      ))}
                    </HookFormSelectionListSingle>
                  </FormField>
                </>
              )}

              {fieldRules.user.visible && (
                <FormField>
                  <HookFormUserSelect
                    name="user"
                    label="Owner"
                    control={form.control}
                    initialArg={{
                      filters: {
                        strategy: 'all',
                        filters: [
                          {
                            field: 'user_type',
                            operator: 'is_one',
                            values: [{ value: 'member' }],
                          },
                        ],
                      },
                      sortBy: { field: 'display_name', order: 'asc' },
                    }}
                    helpIcon={{
                      tooltipClassName: styles.helpTooltip,
                      text: (
                        <Text.Paragraph variant="2" color="inherit">
                          The owner of the dashboard can edit and delete it, or
                          transfer ownership to another user. Only users with
                          full access to Analytics can be owners.{' '}
                          <Text.ExternalLink
                            color="blue.300"
                            onClick={() =>
                              elevioClient.openArticle(
                                articles.analytics.quickViews,
                              )
                            }
                          >
                            Read more.
                          </Text.ExternalLink>
                        </Text.Paragraph>
                      ),
                    }}
                    rules={{
                      required: 'Owner is required',
                      validate: {
                        hasAccess: async (user) => {
                          const userId = user[0].id;

                          // Skip validation if the selected user is the current owner.
                          if (userId === quickView?.user.id) return true;

                          try {
                            const response = await dispatch(
                              usersApi.endpoints.readUserRoles.initiate({
                                userId,
                              }),
                            ).unwrap();

                            const hasAnalyticsScope =
                              response.data.roles_with_scopes.some(
                                ({ role_scopes }) =>
                                  role_scopes.some(
                                    ({ scope }) => scope.value === 'analytics',
                                  ),
                              );

                            return (
                              hasAnalyticsScope ||
                              'The selected user does not have full access to Analytics.'
                            );
                          } catch (error) {
                            return 'Could not validate the selected user.';
                          }
                        },
                      },
                    }}
                    disabled={fieldRules.user.disabled}
                  />
                </FormField>
              )}
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName={texts[mode].button}
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            isLoading: createState.isLoading || updateState.isLoading,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
